import { WritableSignal } from '@angular/core';

export interface DashboardFilterOption<T> {
  value: T;
  /** A translation key, or text shown as is */
  label: string;
}

/** A value shown in the filter bar; layouts list which filters appear and may set their defaults. */
export interface DashboardFilterDefinition<T> {
  readonly kind: 'filter';
  /** A translation key, or text shown as is */
  label: string;
  defaultValue: T;
  control: 'toggle' | 'select';
  options: DashboardFilterOption<T>[];
}

/** Shared state without a control, e.g. a selection one widget makes and others react to. */
export interface DashboardValueDefinition<T> {
  readonly kind: 'value';
  defaultValue: T;
}

export type DashboardStateEntry<T = unknown> = DashboardFilterDefinition<T> | DashboardValueDefinition<T>;

/** A dashboard's shared state by name; the names are what layouts' `filters` and `filterDefaults` refer to. */
export type DashboardStateDefinition = Readonly<Record<string, DashboardStateEntry>>;

/** The state widgets see: one writable signal per entry. Widgets name the part they use as an interface. */
export type DashboardState<D extends DashboardStateDefinition> = {
  readonly [K in keyof D]: WritableSignal<D[K] extends DashboardStateEntry<infer T> ? T : never>;
};

export function dashboardFilter<T>(filter: Omit<DashboardFilterDefinition<T>, 'kind'>): DashboardFilterDefinition<T> {
  return { kind: 'filter', ...filter };
}

export function dashboardValue<T>(defaultValue: T): DashboardValueDefinition<T> {
  return { kind: 'value', defaultValue };
}

export interface DashboardStateField<T = unknown> {
  name: string;
  defaultValue: T;
  /** Present for filters, which the filter bar and the Customize panel show */
  filter?: Omit<DashboardFilterDefinition<T>, 'kind' | 'defaultValue'>;
}

export function stateFieldsOf(definition: DashboardStateDefinition): DashboardStateField[] {
  return Object.entries(definition).map(([name, entry]) =>
    entry.kind === 'filter'
      ? {
          name,
          defaultValue: entry.defaultValue,
          filter: { label: entry.label, control: entry.control, options: entry.options },
        }
      : { name, defaultValue: entry.defaultValue },
  );
}

export function filterDefaultOf<T>(
  field: DashboardStateField<T>,
  filterDefaults: Readonly<Record<string, unknown>> = {},
): T {
  return (field.name in filterDefaults ? filterDefaults[field.name] : field.defaultValue) as T;
}

/** The label of the filter option holding `value`; undefined for non-filters and values no option has */
export function filterValueLabel(field: DashboardStateField, value: unknown): string | undefined {
  return field.filter?.options.find((option) => Object.is(option.value, value))?.label;
}
